export const SITE = {
  name: 'DZ Seguidores',
  url:
    (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ??
    'https://dzseguidores.com',
  locale: 'pt_BR',
  twitter: '@dzseguidores',
  defaultTitle: 'Comprar Seguidores e Curtidas Instagram e TikTok | DZ Seguidores',
  defaultDescription:
    'Compre seguidores e likes para Instagram e TikTok com entrega rápida, pagamento via PIX e garantia de reposição. Aumente seu perfil hoje na DZ Seguidores.',
  keywords: [
    'comprar seguidores instagram',
    'comprar curtidas instagram',
    'comprar likes instagram',
    'comprar seguidores tiktok',
    'comprar curtidas tiktok',
    'comprar likes tiktok',
    'seguidores instagram barato',
    'seguidores tiktok barato',
    'aumentar seguidores instagram',
    'aumentar seguidores tiktok',
    'dz seguidores',
  ],
} as const;

export type SeoInput = {
  title?: string;
  description?: string;
  path?: string;
  keywords?: string[];
  type?: 'website' | 'product' | 'article';
  image?: string | null;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function absoluteUrl(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE.url}${normalized === '/' ? '' : normalized}`;
}

export function applySeo({
  title,
  description,
  path = '/',
  keywords,
  type = 'website',
  image,
  noindex = false,
  jsonLd,
}: SeoInput) {
  const fullTitle = title
    ? title.includes(SITE.name)
      ? title
      : `${title} | ${SITE.name}`
    : SITE.defaultTitle;
  const desc = description ?? SITE.defaultDescription;
  const url = absoluteUrl(path);
  const kw = [...SITE.keywords, ...(keywords ?? [])].join(', ');
  const ogImage = image ? (image.startsWith('http') ? image : absoluteUrl(image)) : undefined;

  document.title = fullTitle;

  upsertMeta('name', 'description', desc);
  upsertMeta('name', 'keywords', kw);
  upsertMeta(
    'name',
    'robots',
    noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
  );
  upsertMeta('name', 'author', SITE.name);
  upsertLink('canonical', url);

  upsertMeta('property', 'og:type', type === 'product' ? 'product' : 'website');
  upsertMeta('property', 'og:site_name', SITE.name);
  upsertMeta('property', 'og:locale', SITE.locale);
  upsertMeta('property', 'og:title', fullTitle);
  upsertMeta('property', 'og:description', desc);
  upsertMeta('property', 'og:url', url);
  if (ogImage) upsertMeta('property', 'og:image', ogImage);

  upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
  upsertMeta('name', 'twitter:title', fullTitle);
  upsertMeta('name', 'twitter:description', desc);
  if (ogImage) upsertMeta('name', 'twitter:image', ogImage);

  if (jsonLd) {
    upsertJsonLd('dz-seo-jsonld', Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
  }
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE.url,
    logo: absoluteUrl('/favicon.svg'),
    description: SITE.defaultDescription,
    areaServed: 'BR',
    availableLanguage: 'pt-BR',
  };
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: SITE.url,
    description: SITE.defaultDescription,
    inLanguage: 'pt-BR',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE.url}/catalogo?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function productJsonLd(input: {
  name: string;
  description: string;
  slug: string;
  priceCents: string | number;
  compareAtCents?: string | number | null;
  imageUrl?: string | null;
  networkName?: string;
}) {
  const price =
    typeof input.priceCents === 'string'
      ? (Number(input.priceCents) / 100).toFixed(2)
      : (input.priceCents / 100).toFixed(2);

  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    url: absoluteUrl(`/produto/${input.slug}`),
    priceCurrency: 'BRL',
    price,
    availability: 'https://schema.org/InStock',
    seller: { '@type': 'Organization', name: SITE.name },
  };

  if (input.compareAtCents != null && Number(input.compareAtCents) > Number(input.priceCents)) {
    offer.priceValidUntil = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: input.name,
    description: input.description,
    sku: input.slug,
    url: absoluteUrl(`/produto/${input.slug}`),
    image: input.imageUrl ? [input.imageUrl] : undefined,
    brand: { '@type': 'Brand', name: SITE.name },
    category: input.networkName
      ? `Seguidores e curtidas ${input.networkName}`
      : 'Seguidores e curtidas redes sociais',
    offers: offer,
  };
}

export function itemListJsonLd(
  items: Array<{ name: string; slug: string; position: number }>,
  listName: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      url: absoluteUrl(`/produto/${item.slug}`),
      name: item.name,
    })),
  };
}

export function breadcrumbJsonLd(crumbs: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

export type ServiceLandingKey =
  | 'instagram'
  | 'instagram-seguidores'
  | 'instagram-curtidas'
  | 'tiktok'
  | 'tiktok-seguidores'
  | 'tiktok-curtidas';

export const SERVICE_LANDINGS: Record<
  ServiceLandingKey,
  {
    path: string;
    network: 'instagram' | 'tiktok';
    serviceType?: 'followers' | 'likes';
    title: string;
    h1: string;
    description: string;
    keywords: string[];
    intro: string;
    bullets: string[];
  }
> = {
  instagram: {
    path: '/instagram',
    network: 'instagram',
    title: 'Comprar Seguidores e Curtidas Instagram',
    h1: 'Comprar seguidores e curtidas no Instagram',
    description:
      'Compre seguidores e likes para Instagram com entrega monitorada, PIX e garantia de reposição. Cresça seu perfil agora na DZ Seguidores.',
    keywords: [
      'comprar seguidores instagram',
      'comprar curtidas instagram',
      'likes instagram',
      'aumentar seguidores instagram',
    ],
    intro:
      'Pacotes de seguidores e curtidas para Instagram pensados para quem quer prova social rápida, sem senha e com suporte em português.',
    bullets: [
      'Seguidores e curtidas para feed, Reels e stories',
      'Entrega gradual e monitorada',
      'Pagamento seguro via PIX ou cartão',
      'Garantia de reposição conforme o pacote',
    ],
  },
  'instagram-seguidores': {
    path: '/instagram/seguidores',
    network: 'instagram',
    serviceType: 'followers',
    title: 'Comprar Seguidores Instagram Barato e Rápido',
    h1: 'Comprar seguidores Instagram',
    description:
      'Compre seguidores Instagram com entrega rápida e garantia. Pacotes para crescer seu perfil com segurança na DZ Seguidores.',
    keywords: [
      'comprar seguidores instagram',
      'seguidores instagram barato',
      'aumentar seguidores instagram',
      'comprar seguidores ig',
    ],
    intro:
      'Escolha o volume de seguidores Instagram ideal para o seu perfil. Entrega monitorada, sem pedir senha — só o link do perfil.',
    bullets: [
      'Pacotes a partir de volumes pequenos até alto alcance',
      'Ideal para lançamentos e autoridade digital',
      'Checkout em minutos com PIX',
      'Acompanhamento do pedido na sua conta',
    ],
  },
  'instagram-curtidas': {
    path: '/instagram/curtidas',
    network: 'instagram',
    serviceType: 'likes',
    title: 'Comprar Curtidas e Likes Instagram',
    h1: 'Comprar curtidas Instagram',
    description:
      'Compre likes e curtidas Instagram para posts e Reels. Entrega rápida, PIX e garantia de reposição na DZ Seguidores.',
    keywords: [
      'comprar curtidas instagram',
      'comprar likes instagram',
      'likes instagram barato',
      'aumentar curtidas instagram',
    ],
    intro:
      'Impulsione posts e Reels com curtidas Instagram. Mais engajamento visível ajuda no alcance orgânico e na prova social.',
    bullets: [
      'Curtidas para posts e Reels',
      'Sem senha — apenas o link da publicação',
      'Entrega rápida e acompanhada',
      'Suporte humano em português',
    ],
  },
  tiktok: {
    path: '/tiktok',
    network: 'tiktok',
    title: 'Comprar Seguidores e Curtidas TikTok',
    h1: 'Comprar seguidores e curtidas no TikTok',
    description:
      'Compre seguidores e likes para TikTok com entrega rápida, pagamento via PIX e garantia. Cresça no TikTok com a DZ Seguidores.',
    keywords: [
      'comprar seguidores tiktok',
      'comprar curtidas tiktok',
      'likes tiktok',
      'aumentar seguidores tiktok',
    ],
    intro:
      'Serviços de seguidores e curtidas TikTok para quem quer crescer no For You com prova social e entrega monitorada.',
    bullets: [
      'Seguidores e likes para perfil e vídeos',
      'Entrega monitorada ponta a ponta',
      'PIX ou cartão no checkout',
      'Garantia de reposição nos pacotes elegíveis',
    ],
  },
  'tiktok-seguidores': {
    path: '/tiktok/seguidores',
    network: 'tiktok',
    serviceType: 'followers',
    title: 'Comprar Seguidores TikTok Barato e Rápido',
    h1: 'Comprar seguidores TikTok',
    description:
      'Compre seguidores TikTok com entrega rápida e segura. Aumente sua audiência e autoridade no app com a DZ Seguidores.',
    keywords: [
      'comprar seguidores tiktok',
      'seguidores tiktok barato',
      'aumentar seguidores tiktok',
      'comprar followers tiktok',
    ],
    intro:
      'Monte seu crescimento no TikTok com pacotes de seguidores sob medida. Sem senha, só o link do perfil.',
    bullets: [
      'Crescimento de base de seguidores',
      'Ideal para creators e marcas',
      'Pagamento via PIX em segundos',
      'Pedidos com rastreio na conta',
    ],
  },
  'tiktok-curtidas': {
    path: '/tiktok/curtidas',
    network: 'tiktok',
    serviceType: 'likes',
    title: 'Comprar Curtidas e Likes TikTok',
    h1: 'Comprar curtidas TikTok',
    description:
      'Compre likes e curtidas TikTok para seus vídeos. Mais engajamento, entrega rápida e garantia na DZ Seguidores.',
    keywords: [
      'comprar curtidas tiktok',
      'comprar likes tiktok',
      'likes tiktok barato',
      'aumentar curtidas tiktok',
    ],
    intro:
      'Dê impulso aos seus vídeos com curtidas TikTok. Engajamento inicial ajuda a aparecer para mais pessoas.',
    bullets: [
      'Likes para vídeos e posts',
      'Link público apenas — sem senha',
      'Entrega rápida e monitorada',
      'Suporte em português',
    ],
  },
};

export const HOME_FAQS = [
  {
    q: 'É seguro comprar seguidores e curtidas Instagram ou TikTok?',
    a: 'Sim. Na DZ Seguidores você nunca informa senha — apenas o link público do perfil ou da publicação. Pagamentos rodam via PIX ou cartão com parceiros certificados.',
  },
  {
    q: 'Quanto tempo demora para receber seguidores ou likes?',
    a: 'A maioria dos pedidos inicia em até 30 minutos. O prazo total depende do volume do pacote de seguidores ou curtidas.',
  },
  {
    q: 'Vocês oferecem garantia de reposição?',
    a: 'Sim. Se houver queda dentro da política do produto, fazemos a reposição conforme as regras do pacote adquirido.',
  },
  {
    q: 'Posso comprar só curtidas Instagram ou só seguidores TikTok?',
    a: 'Sim. Temos pacotes específicos de seguidores e de curtidas (likes) para Instagram e TikTok no catálogo.',
  },
] as const;
